import {
  useMutation,
  UseMutationOptions,
  useQueryClient,
} from '@tanstack/react-query';

import { gqlClient } from '@/api/graphql-client';
import projectKeys from '@/api/queries/project/keyFactory';
import proposalKeys from '@/api/queries/proposal/keyFactor';
import { graphql } from '@/gql';
import { AddProposalGrantInput, AddProposalGrantMutation } from '@/gql/graphql';

const ADD_PROPOSAL_GRANT_MUTATION = graphql(`
  mutation AddProposalGrant($input: AddProposalGrantInput!) {
    addProposalGrant(input: $input) {
      id
    }
  }
`);

/**
 * Attach a catalogue grant to the Proposal. The applied amount is derived
 * server-side from what the gross leaves after earlier grants, so the response
 * is not authoritative for anything the builder shows — both caches are
 * invalidated and re-read instead.
 */
const useAddProposalGrant = (args?: {
  proposalId?: string;
  projectId?: string;
  options?: UseMutationOptions<
    AddProposalGrantMutation,
    unknown,
    AddProposalGrantInput
  >;
}) => {
  const queryClient = useQueryClient();
  const { onSuccess, ...restOptions } = args?.options ?? {};

  return useMutation({
    mutationFn: (input: AddProposalGrantInput) => {
      return gqlClient.request(ADD_PROPOSAL_GRANT_MUTATION, { input });
    },
    onSuccess: async (data, variables, context) => {
      if (args?.proposalId) {
        await queryClient.invalidateQueries({
          queryKey: proposalKeys.proposal(args.proposalId),
        });
      }
      if (args?.projectId) {
        void queryClient.invalidateQueries({
          queryKey: projectKeys.profitBreakdown(args.projectId),
        });
      }
      onSuccess?.(data, variables, context);
    },
    ...restOptions,
  });
};

export default useAddProposalGrant;
